import { normalizeEmail } from "../auth";
import type { RuntimeEnv } from "../env";

export type EmailMessage = {
  to: string[];
  subject: string;
  text: string;
};

export type EmailProvider = {
  name: string;
  send: (message: EmailMessage) => Promise<void>;
};

export type DeliveryPlan = {
  action: "send" | "redirect" | "block";
  to: string[];
};

/**
 * No network provider is configured. Auth mail stays on Supabase's built-in
 * mailer. Application mail is recorded in the in-app inbox until a provider
 * is added in this module only.
 */
export const unconfiguredEmailProvider: EmailProvider = {
  name: "unconfigured",
  async send() {
    throw new Error("No email provider is configured.");
  },
};

export function redirectMailbox(value: string | undefined): string | null {
  const email = normalizeEmail(value ?? "");
  if (!email.includes("@") || email.startsWith("@") || email.endsWith("@")) {
    return null;
  }
  return email;
}

export function planDelivery(
  to: string[],
  runtime: RuntimeEnv,
  redirect: string | undefined,
): DeliveryPlan {
  if (runtime.misconfigured || runtime.name !== "production") {
    if (runtime.name === "qa") {
      const mailbox = redirectMailbox(redirect);
      if (mailbox) return { action: "redirect", to: [mailbox] };
    }
    return { action: "block", to: [] };
  }
  return { action: "send", to };
}

export function guardStoredMail<T extends { to: string[]; subject: string }>(
  message: T,
  runtime: RuntimeEnv,
  redirect: string | undefined,
): T {
  if (runtime.name !== "qa") return message;
  const plan = planDelivery(message.to, runtime, redirect);
  const prefix = plan.action === "redirect" ? "[QA redirected] " : "[QA not sent] ";
  const subject = message.subject.startsWith(prefix)
    ? message.subject
    : `${prefix}${message.subject}`;
  return { ...message, to: plan.to, subject };
}

export function assertSafeOutbound(
  to: string[],
  runtime: RuntimeEnv,
  redirect: string | undefined,
): void {
  const plan = planDelivery(to, runtime, redirect);
  if (plan.action === "block") {
    if (to.length > 0) {
      throw new Error("Refusing to send email from this environment.");
    }
    return;
  }
  if (plan.action === "redirect") {
    const mailbox = plan.to[0];
    if (!mailbox || to.some((address) => normalizeEmail(address) !== mailbox)) {
      throw new Error(
        "Refusing to send email to anyone except the QA redirect mailbox.",
      );
    }
  }
}

export async function deliverEmail(
  message: EmailMessage,
  runtime: RuntimeEnv,
  redirect: string | undefined,
  provider: EmailProvider = unconfiguredEmailProvider,
): Promise<{ delivered: boolean; to: string[]; reason: string }> {
  const plan = planDelivery(message.to, runtime, redirect);
  if (plan.action === "block") {
    return { delivered: false, to: [], reason: "blocked" };
  }
  assertSafeOutbound(plan.to, runtime, redirect);
  if (provider.name === "unconfigured") {
    return {
      delivered: false,
      to: plan.to,
      reason: "provider-not-configured",
    };
  }
  await provider.send({ ...message, to: plan.to });
  return { delivered: true, to: plan.to, reason: "sent" };
}
